import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  DndContext, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Columns3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Gestão de colunas (redimensionar, reordenar, mostrar/ocultar) reutilizável em qualquer
// tabela do tipo planilha/CRM — preferência salva no localStorage do navegador, por tabela
// (cada `storageKey` é independente).

export interface ColunaDef<Id extends string = string> {
  id: Id;
  label: string;
  defaultWidth: number;
  minWidth: number;
  defaultVisible: boolean;
  /** Não pode ser ocultada (ex: a coluna que identifica a linha). */
  travada?: boolean;
}

interface PreferenciasColunas<Id extends string> {
  ordem: Id[];
  larguras: Record<string, number>;
  visiveis: Record<string, boolean>;
}

function preferenciasPadrao<Id extends string>(defs: ColunaDef<Id>[]): PreferenciasColunas<Id> {
  return {
    ordem: defs.map(c => c.id),
    larguras: Object.fromEntries(defs.map(c => [c.id, c.defaultWidth])),
    visiveis: Object.fromEntries(defs.map(c => [c.id, c.defaultVisible])),
  };
}

function carregarPreferencias<Id extends string>(defs: ColunaDef<Id>[], storageKey: string): PreferenciasColunas<Id> {
  const padrao = preferenciasPadrao(defs);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return padrao;
    const salvo = JSON.parse(raw) as Partial<PreferenciasColunas<Id>>;
    const idsValidos = new Set(defs.map(c => c.id));
    const ordemSalva = (salvo.ordem || []).filter((id): id is Id => idsValidos.has(id as Id));
    const faltantes = padrao.ordem.filter(id => !ordemSalva.includes(id));
    return {
      ordem: [...ordemSalva, ...faltantes],
      larguras: { ...padrao.larguras, ...salvo.larguras },
      visiveis: { ...padrao.visiveis, ...salvo.visiveis },
    };
  } catch {
    return padrao;
  }
}

function salvarPreferencias<Id extends string>(storageKey: string, prefs: PreferenciasColunas<Id>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch {
    // localStorage indisponível (aba privada, storage bloqueado) — preferência só não persiste.
  }
}

export function useGerenciadorColunas<Id extends string>(defs: ColunaDef<Id>[], storageKey: string) {
  const [ordem, setOrdem] = useState<Id[]>(() => carregarPreferencias(defs, storageKey).ordem);
  const [larguras, setLarguras] = useState<Record<string, number>>(() => carregarPreferencias(defs, storageKey).larguras);
  const [visiveis, setVisiveis] = useState<Record<string, boolean>>(() => carregarPreferencias(defs, storageKey).visiveis);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    salvarPreferencias(storageKey, { ordem, larguras, visiveis });
  }, [storageKey, ordem, larguras, visiveis]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setOrdem(prev => {
      const oldIndex = prev.indexOf(active.id as Id);
      const newIndex = prev.indexOf(over.id as Id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const redimensionar = (id: Id, deltaX: number) => {
    const def = defs.find(c => c.id === id);
    if (!def) return;
    setLarguras(prev => {
      const atual = prev[id] ?? def.defaultWidth;
      return { ...prev, [id]: Math.max(def.minWidth, atual + deltaX) };
    });
  };

  const visiveisOrdenadas = ordem
    .filter(id => visiveis[id] !== false)
    .map(id => {
      const def = defs.find(c => c.id === id)!;
      return { id, width: larguras[id] ?? def.defaultWidth, label: def.label };
    });

  return {
    defs, ordem, visiveis, setVisiveis, menuOpen, setMenuOpen,
    sensors, handleDragEnd, redimensionar, visiveisOrdenadas,
  };
}

export type GerenciadorColunas<Id extends string> = ReturnType<typeof useGerenciadorColunas<Id>>;

// Alça de redimensionar coluna: arrasta a borda direita do cabeçalho pra mudar a largura.
export function ColunaResizeHandle({ onResize }: { onResize: (deltaX: number) => void }) {
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    let ultimoX = e.clientX;
    const onMove = (ev: PointerEvent) => {
      const delta = ev.clientX - ultimoX;
      ultimoX = ev.clientX;
      onResizeRef.current(delta);
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  return (
    <span
      onPointerDown={onPointerDown}
      onClick={e => e.stopPropagation()}
      className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none hover:bg-primary/40 active:bg-primary/60"
    />
  );
}

function ColunaListItem<Id extends string>({
  def, visivel, onToggleVisivel,
}: {
  def: ColunaDef<Id>;
  visivel: boolean;
  onToggleVisivel: (checked: boolean) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: def.id });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex items-center gap-2 rounded px-1.5 py-1', isDragging ? 'opacity-40' : 'hover:bg-muted')}
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab active:cursor-grabbing p-0.5 rounded touch-none"
        title="Arrastar para reordenar"
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <Checkbox
        checked={visivel}
        disabled={def.travada}
        onCheckedChange={checked => onToggleVisivel(checked === true)}
      />
      <span className={cn('text-sm', def.travada && 'text-muted-foreground')}>
        {def.label}{def.travada ? ' (sempre visível)' : ''}
      </span>
    </div>
  );
}

export function BotaoColunas<Id extends string>({
  gerenciador, className,
}: {
  gerenciador: GerenciadorColunas<Id>;
  /** Sobrepõe a altura/estilo padrão do botão (h-8) — ex: "h-10" pra alinhar com um Select ao lado. */
  className?: string;
}) {
  const { defs, ordem, visiveis, setVisiveis, menuOpen, setMenuOpen, sensors, handleDragEnd } = gerenciador;
  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn('h-8 gap-1 text-xs', className)}>
          <Columns3 className="h-3.5 w-3.5" />Colunas
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <p className="text-xs text-muted-foreground px-1.5 pb-1.5">
          Arraste para reordenar. Marque para mostrar/ocultar.
        </p>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ordem} strategy={verticalListSortingStrategy}>
            <div className="space-y-0.5">
              {ordem.map(id => {
                const def = defs.find(c => c.id === id)!;
                return (
                  <ColunaListItem
                    key={id}
                    def={def}
                    visivel={visiveis[id] !== false}
                    onToggleVisivel={checked => setVisiveis(prev => ({ ...prev, [id]: checked }))}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  );
}
